import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Slider } from '@ardoq/forms';
import { TraversalInfoGrid } from './TraversalInfoGrid';
import { text2, text2Bold } from '@ardoq/typography';
import { ExternalLink } from '@ardoq/link';
import { colors, s32 } from '@ardoq/design-tokens';
import { TraversalInfoMap } from './TraversalInfoMap';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { useOnMount } from '@ardoq/hooks';
import { getLoadedPersonalSetting } from 'streams/currentUser/getLoadedPersonalSetting';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { updateViewSettings } from '@ardoq/view-settings';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { trackEvent } from 'tracking/tracking';
import { currentTimestamp, parseToTimestamp } from '@ardoq/date-time';
import { Trans, t, type WithI18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { map } from 'rxjs';

const Container = styled.div`
  ${text2};
  font-weight: normal;
  width: 528px;
`;
const Row = styled.div`
  display: flex;
  justify-items: stretch;
  justify-content: center;
  align-items: center;
`;

const SliderContainer = styled.div`
  width: 208px;
  height: 40px;
  margin-left: 26px;
  display: flex;
  align-items: center;
  > div {
    width: 100%;
  }
  > div:last-child > div:last-child > div:last-child {
    /* slider value label */
    ${text2Bold};
    align-self: center;
  }
`;
const Label = styled.label`
  ${text2}
  font-weight: normal;
`;
const Bold = styled.span`
  ${text2Bold}
`;
let checkedUserSettings = false;
type TraversalInfoPopoverProps = WithI18nLocale;
const TraversalInfoPopoverBase = ({
  i18nLocale,
}: TraversalInfoPopoverProps) => {
  /** need to use useCallback for this because React forbids async functions in useEffect. */
  const checkUserSettings = useCallback(async () => {
    if (checkedUserSettings) {
      return;
    }
    const viewId = ViewIds.DEPENDENCY_MAP_2;
    const seenTraversalInfo = await getLoadedPersonalSetting(
      PersonalSetting.DEPENDENCY_MAP_SEEN_TRAVERSAL_INFO
    );
    if (typeof seenTraversalInfo !== 'boolean' || !seenTraversalInfo) {
      const seenTraversalInfoTime = !seenTraversalInfo
        ? false
        : parseToTimestamp(seenTraversalInfo);
      trackEvent('Viewed traversal info popover', {
        elapsedMillisecondsSinceUserOpenedTraversalOptionsWithBadge:
          seenTraversalInfoTime
            ? currentTimestamp() - seenTraversalInfoTime
            : 0,
      });
      dispatchAction(
        updateViewSettings({
          viewId,
          settings: { seenTraversalInfo: true },
          persistent: true,
        })
      );
    }
    checkedUserSettings = true;
  }, []);
  useOnMount(() => {
    checkUserSettings();
  });

  const [inDegrees, setInDegrees] = useState(3);
  const [outDegrees, setOutDegrees] = useState(3);
  const [hoveredComponentId, setHoveredComponentId] = useState<string | null>(
    null
  );
  return (
    <Container>
      <Label>
        {t(
          i18nLocale,
          'tabview:dependencyMap.traversalInfo.directionMatters',
          'For the incoming and outgoing degrees of relationship the direction does matter.'
        )}
      </Label>
      <Label>
        <Trans
          i18nKey="tabview:dependencyMap.traversalInfo.sameDirectionOnly"
          defaults="It only includes components which are <bold>connected in the same direction</bold>."
          components={{ bold: <Bold /> }}
        />
      </Label>
      <Row style={{ marginTop: s32 }}>
        <Label>
          {t(
            i18nLocale,
            'tabview:dependencyMap.traversalInfo.incoming',
            'Incoming:'
          )}
        </Label>
        <SliderContainer>
          <Slider
            minValue={0}
            maxValue={3}
            value={inDegrees}
            onChange={setInDegrees}
            isValueLabelVisible={true}
            thumbColor={colors.green600}
            barColor={colors.grey300}
            valueBarColor={colors.grey300}
          />
        </SliderContainer>
      </Row>
      <Row style={{ marginBottom: s32 }}>
        <Label>
          {t(
            i18nLocale,
            'tabview:dependencyMap.traversalInfo.outgoing',
            'Outgoing:'
          )}
        </Label>
        <SliderContainer>
          <Slider
            minValue={0}
            maxValue={3}
            value={outDegrees}
            onChange={setOutDegrees}
            isValueLabelVisible={true}
            thumbColor={colors.purple35}
            barColor={colors.grey300}
            valueBarColor={colors.grey300}
          />
        </SliderContainer>
      </Row>
      <Row style={{ marginBottom: s32 }}>
        <TraversalInfoGrid
          inDegrees={inDegrees}
          outDegrees={outDegrees}
          hoveredComponentId={hoveredComponentId}
          setHoveredComponentId={setHoveredComponentId}
        />
        <TraversalInfoMap
          inDegrees={inDegrees}
          outDegrees={outDegrees}
          hoveredComponentId={hoveredComponentId}
          setHoveredComponentId={setHoveredComponentId}
        />
      </Row>
      <ExternalLink
        opensInNewTabLabel={t(
          i18nLocale,
          'common:labels.opensInNewTab',
          '(new tab)'
        )}
        href={KnowledgeBaseLink.DEPENDENCY_MAP_2}
      >
        {t(
          i18nLocale,
          'tabview:dependencyMap.traversalInfo.knowledgeBaseLink',
          'To learn more about degrees of relationships, read our knowledge base article.'
        )}
      </ExternalLink>
    </Container>
  );
};
export const TraversalInfoPopover = connect(
  TraversalInfoPopoverBase,
  i18nLocale$.pipe(map(i18nLocale => ({ i18nLocale })))
);
